import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { StatusBadge, PriorityBadge } from '../components/StatusBadge';
import { formatDate } from '../utils/dateUtils';
import { useSessionState } from '../hooks/useSessionState';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Search, Eye, Clock, AlertTriangle, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SLA_COUNTDOWN = (deadlineISO) => {
  if (!deadlineISO) return null;
  const ms = new Date(deadlineISO).getTime() - Date.now();
  if (ms <= 0) return { breached: true, label: 'BREACHED' };
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return { breached: false, label: `${h}h ${m}m left` };
};

// SLA outcome for a ticket whose lifecycle is done (Completed / Escalated).
// Returns null if not finalized yet.
const SLA_OUTCOME = (ticket) => {
  if (!ticket) return null;
  if (ticket.status === 'Escalated') return { variant: 'destructive', label: 'Breached' };
  if (ticket.status === 'Completed' && ticket.sla_deadline) {
    const completedAt = ticket.completed_at || ticket.updated_at;
    if (!completedAt) return null;
    const onTime = new Date(completedAt).getTime() <= new Date(ticket.sla_deadline).getTime();
    return onTime
      ? { variant: 'success', label: 'On Time' }
      : { variant: 'destructive', label: 'Breached' };
  }
  return null;
};

export default function ITQueuePage() {
  const navigate = useNavigate();
  const { getAuthHeader, employee } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useSessionState('it-queue:search', '');
  const [activeTab, setActiveTab] = useSessionState('it-queue:tab', 'all');

  const role = employee?.role;
  const canAccess = ['IT Team', 'IT Head', 'Super Admin', 'Management'].includes(role);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/it/queue`, { headers: getAuthHeader() });
      if (res.ok) {
        const data = await res.json();
        setTickets(data);
      }
    } catch (e) {
      console.error('Fetch IT queue error:', e);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    if (canAccess) fetchTickets();
  }, [canAccess, fetchTickets]);

  // Refresh every 30s so SLA countdowns stay current
  useEffect(() => {
    if (!canAccess) return;
    const id = setInterval(fetchTickets, 30000);
    return () => clearInterval(id);
  }, [canAccess, fetchTickets]);

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return tickets.filter(t => {
      const matchesSearch =
        t.ticket_id?.toLowerCase().includes(q) ||
        t.request_type?.toLowerCase().includes(q) ||
        t.requester_name?.toLowerCase().includes(q);
      let matchesTab = true;
      if (activeTab === 'open') matchesTab = t.status === 'Open';
      else if (activeTab === 'in-progress') matchesTab = t.status === 'In Progress';
      else if (activeTab === 'escalated') matchesTab = t.status === 'Escalated';
      else if (activeTab === 'completed') matchesTab = t.status === 'Completed';
      else if (activeTab === 'sla-risk') {
        const sla = SLA_COUNTDOWN(t.sla_deadline);
        matchesTab = sla && !sla.breached && ['Open', 'In Progress'].includes(t.status);
      }
      return matchesSearch && matchesTab;
    });
  }, [tickets, searchQuery, activeTab]);

  if (!canAccess) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Access denied. IT role required.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="it-queue-page">
        <div>
          <h1 className="text-3xl font-bold">IT Queue</h1>
          <p className="text-muted-foreground mt-1">All IT tickets assigned to your location</p>
        </div>

        <Card>
          <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle>IT Tickets</CardTitle>
              <CardDescription>Track SLA, status, and assignments</CardDescription>
            </div>
            <div className="relative w-full md:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search ticket id, type, requester…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="it-queue-search"
              />
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-4">
              <TabsList>
                <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
                <TabsTrigger value="open" data-testid="tab-open">Open</TabsTrigger>
                <TabsTrigger value="in-progress" data-testid="tab-in-progress">In Progress</TabsTrigger>
                <TabsTrigger value="sla-risk" data-testid="tab-sla-risk">SLA Risk</TabsTrigger>
                <TabsTrigger value="escalated" data-testid="tab-escalated">Escalated</TabsTrigger>
                <TabsTrigger value="completed" data-testid="tab-completed">Completed</TabsTrigger>
              </TabsList>
            </Tabs>

            {loading ? (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Loader2 className="w-8 h-8 mx-auto mb-2 opacity-40" />
                No IT tickets found.
              </div>
            ) : (
              <div className="border rounded-lg overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ticket</TableHead>
                      <TableHead>Request Type</TableHead>
                      <TableHead>Requester</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Assigned To</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>SLA</TableHead>
                      <TableHead>Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map(t => {
                      const sla = SLA_COUNTDOWN(t.sla_deadline);
                      return (
                        <TableRow
                          key={t.ticket_id}
                          data-testid={`it-queue-row-${t.ticket_id}`}
                          className="cursor-pointer hover:bg-muted/40"
                          onClick={() => navigate(`/tickets/${t.ticket_id}`)}
                        >
                          <TableCell>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); navigate(`/tickets/${t.ticket_id}`); }}
                              className="font-mono text-xs text-primary hover:underline"
                              data-testid={`open-${t.ticket_id}`}
                            >
                              {t.ticket_id}
                            </button>
                          </TableCell>
                          <TableCell>{t.request_type}</TableCell>
                          <TableCell>{t.requester_name}</TableCell>
                          <TableCell>{t.ticket_location || '-'}</TableCell>
                          <TableCell className="text-xs">{t.assigned_it_name || <span className="text-muted-foreground">Unassigned</span>}</TableCell>
                          <TableCell><PriorityBadge priority={t.priority} /></TableCell>
                          <TableCell><StatusBadge status={t.status} /></TableCell>
                          <TableCell>
                            {(() => {
                              const outcome = SLA_OUTCOME(t);
                              if (outcome) {
                                return (
                                  <Badge
                                    variant={outcome.variant === 'success' ? 'outline' : 'destructive'}
                                    className={`gap-1 ${outcome.variant === 'success' ? 'border-emerald-500 text-emerald-700 dark:text-emerald-400' : ''}`}
                                  >
                                    {outcome.variant === 'success'
                                      ? <Clock className="w-3 h-3" />
                                      : <AlertTriangle className="w-3 h-3" />}
                                    {outcome.label}
                                  </Badge>
                                );
                              }
                              return sla ? (
                                <Badge variant={sla.breached ? 'destructive' : 'outline'} className="gap-1">
                                  <Clock className="w-3 h-3" />{sla.label}
                                </Badge>
                              ) : <span className="text-xs text-muted-foreground">-</span>;
                            })()}
                          </TableCell>
                          <TableCell className="text-xs">{formatDate(t.created_at)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
